<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-container whats-new-modal">

      <!-- Header -->
      <div class="modal-header">
        <div class="header-content">
          <img src="/accessiq-favicon.png" alt="AccessIQ" class="header-logo" />
          <div>
            <h2>What's New</h2>
            <p class="subtitle">Recent updates to the platform</p>
          </div>
        </div>
        <div class="header-actions">
          <button
            v-if="isPlatformAdmin"
            class="btn-view-toggle"
            :class="{ active: previewUserView }"
            @click="previewUserView = !previewUserView"
            :title="previewUserView ? 'Switch to admin view' : 'Preview what users see'"
          >
            <i :class="previewUserView ? 'fas fa-user' : 'fas fa-shield-alt'"></i>
            {{ previewUserView ? 'User view' : 'Admin view' }}
          </button>
          <button class="close-btn" @click="$emit('close')">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <!-- Date range picker -->
      <div class="date-bar">
        <i class="fas fa-calendar-alt date-icon"></i>
        <label class="date-label">From</label>
        <input type="date" v-model="dateFrom" class="date-input" @change="loadTickets" />
        <label class="date-label">To</label>
        <input type="date" v-model="dateTo" class="date-input" @change="loadTickets" />
        <span class="ticket-count" v-if="!loading">
          {{ tickets.length }} update{{ tickets.length !== 1 ? 's' : '' }}
        </span>
      </div>

      <!-- Body -->
      <div class="modal-body">

        <!-- Loading -->
        <div v-if="loading" class="loading-state">
          <div v-for="n in 3" :key="n" class="skeleton-row"></div>
        </div>

        <!-- Empty -->
        <div v-else-if="tickets.length === 0" class="empty-state">
          <i class="fas fa-check-circle empty-icon"></i>
          <p>No updates in this date range.</p>
          <button class="btn-look-back" @click="lookFurther">Look back 7 more days</button>
        </div>

        <!-- Grouped tickets -->
        <template v-else>
          <div v-for="group in groupedTickets" :key="group.area" class="wn-group">
            <div class="wn-group-header" @click="toggleGroup(group.area)">
              <i class="fas fa-layer-group"></i>
              {{ group.area }}
              <span class="wn-group-count">{{ group.items.length }}</span>
              <i
                class="fas fa-chevron-down wn-chevron"
                :class="{ collapsed: collapsedGroups.has(group.area) }"
              ></i>
            </div>
            <div v-show="!collapsedGroups.has(group.area)" class="wn-group-body">
              <div
                v-for="ticket in group.items"
                :key="ticket.id"
                class="wn-card"
              >
                <div class="wn-card-top">
                  <h3 class="wn-title">{{ displayText(ticket.title) }}</h3>
                  <span class="wn-date">{{ formatDate(ticket.created_at) }}</span>
                </div>
                <p class="wn-desc">{{ displayText(ticket.description) }}</p>
                <div v-if="ticket.ai_plan" class="wn-insight">
                  <i class="fas fa-comment-dots"></i>
                  <span>{{ displayText(ticket.ai_plan) }}</span>
                </div>
              </div>
            </div>
          </div>
        </template>

      </div>

      <div class="modal-footer">
        <button class="btn-got-it" @click="$emit('close')">Got it!</button>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import observabilityAPI from '@/services/observabilityAPI';
import authService from '@/services/auth';

defineEmits(['close']);

// ── Role ─────────────────────────────────────────────────────────────
const isPlatformAdmin = authService.hasPlatformAccess();
const previewUserView = ref(false);

// ── Date range (default: last 3 days) ───────────────────────────────
function isoDate(d) {
  return d.toISOString().split('T')[0];
}

const today = new Date();
const threeDaysAgo = new Date(today);
threeDaysAgo.setDate(today.getDate() - 3);

const dateFrom = ref(isoDate(threeDaysAgo));
const dateTo   = ref(isoDate(today));

// ── Data ─────────────────────────────────────────────────────────────
const tickets = ref([]);
const loading = ref(true);

async function loadTickets() {
  loading.value = true;
  try {
    tickets.value = await observabilityAPI.getWhatsNewTickets(dateFrom.value, dateTo.value);
  } catch (e) {
    console.error('What\'s New load failed:', e);
    tickets.value = [];
  } finally {
    loading.value = false;
  }
}

onMounted(loadTickets);

// ── "Look back" shortcut ─────────────────────────────────────────────
function lookFurther() {
  const current = new Date(dateFrom.value);
  current.setDate(current.getDate() - 7);
  dateFrom.value = isoDate(current);
  loadTickets();
}

// ── Grouping ─────────────────────────────────────────────────────────
const INFRA_MODULES = [
  'Source Management & Scraping', 'LLM Pipeline', 'Celery / Task Queue',
  'API & Data Models', 'Pilot & Deployment', 'Authentication & Security',
];

const MODULE_ORDER = [
  'Briefing Editor', 'Docket IQ', 'Fact Checker', 'IQ Hub / Ticker',
  'Pollie', 'Policy Engine', 'AIQ Actionable Intel', 'Community IQ',
  'Advocacy IQ', 'What-If IQ', 'Keep Me Covered', 'State IQ', 'IWI Board',
  'Document Review', 'Platform / Auth',
  ...INFRA_MODULES,
  'Other',
];

const groupedTickets = computed(() => {
  const hideInfra = isPlatformAdmin && previewUserView.value;
  const map = {};
  for (const t of tickets.value) {
    const area = t.feature_area || 'Other';
    if (hideInfra && INFRA_MODULES.includes(area)) continue;
    if (!map[area]) map[area] = [];
    map[area].push(t);
  }
  return MODULE_ORDER
    .filter(a => map[a]?.length)
    .map(a => ({ area: a, items: map[a] }));
});

// ── Public-facing language cleaner ───────────────────────────────────
const PUBLIC_SUBSTITUTIONS = [
  [/\brolled\s+back\b/gi, 'temporarily reverted to the previous version'],
  [/\broll\s*back\b/gi, 'revert to the previous version'],
  [/\btech\s+debt\b/gi, 'foundational improvements'],
  [/\bAPI\s+timeout\b/gi, 'connection took too long to respond'],
  [/\bmemory\s+leak\b/gi, 'issue causing slowdown over time — now resolved'],
  [/\brate\s+limit(ing)?\b/gi, 'request limits'],
  [/\bnull\s+pointer\b/gi, 'unexpected behavior'],
  [/\broot\s+cause\b/gi, 'what was happening behind the scenes'],
  [/\bknown\s+issue\b/gi, "something we're actively working on"],
  [/\bregression\b/gi, 'behavior introduced in a recent update — now corrected'],
  [/\bdeploy(ment)?\b/gi, 'release'],
  [/\bhotfix\b/gi, 'rapid update'],
  [/\bdeprecated\b/gi, 'being phased out'],
  [/\brefactored\b/gi, 'rebuilt to be faster and more reliable'],
  [/\bworkaround\b/gi, 'interim solution'],
  [/\bmitigation\b/gi, 'resolution'],
  [/\btriage\b/gi, 'review'],
  [/\bincident\b/gi, 'disruption'],
  [/\boutage\b/gi, 'service interruption'],
  [/\bdegraded\b/gi, 'performing below our standards'],
  [/\blatency\b/gi, 'response time'],
  [/\bcrash(ed|es|ing)?\b/gi, 'unexpected interruption'],
  [/\bexception\b/gi, 'unexpected behavior'],
  [/\berrors?\b/gi, 'unexpected behavior'],
  [/\bfailed\b/gi, "didn't complete as expected"],
  [/\bpatch\b/gi, 'update'],
  [/\bgremlins?\b/gi, 'issue'],
  [/\bbugs?\b/gi, 'issues'],
];

function cleanPublicText(text) {
  if (!text) return '';
  let result = text;
  for (const [pattern, replacement] of PUBLIC_SUBSTITUTIONS) {
    result = result.replace(pattern, replacement);
  }
  return result;
}

// Apply public substitutions on top of IWI-prefix strip when previewing user view
function displayText(text) {
  const base = cleanText(text);
  return (isPlatformAdmin && previewUserView.value) ? cleanPublicText(base) : base;
}

// ── Collapse / expand ────────────────────────────────────────────────
const collapsedGroups = ref(new Set());
function toggleGroup(area) {
  const next = new Set(collapsedGroups.value);
  next.has(area) ? next.delete(area) : next.add(area);
  collapsedGroups.value = next;
}

// ── Helpers ──────────────────────────────────────────────────────────
function formatDate(dt) {
  if (!dt) return '';
  return new Date(dt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const IWI_PREFIX = /^i wonder if\s*/i;
function cleanText(text) {
  if (!text) return '';
  return text.replace(IWI_PREFIX, '').replace(/^./, c => c.toUpperCase());
}
</script>

<style scoped>
.modal-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.whats-new-modal {
  background: #ffffff;
  border-radius: 16px;
  width: 100%;
  max-width: 640px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0,0,0,0.18);
  overflow: hidden;
}

/* ── Header ── */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #d9e2ef;
}
.header-content { display: flex; align-items: center; gap: 14px; }
.header-logo { width: 36px; height: 36px; object-fit: contain; flex-shrink: 0; }
.modal-header h2 { font-size: 20px; font-weight: 700; color: #212938; margin: 0 0 2px; }
.subtitle { font-size: 13px; color: #6b7a99; margin: 0; }
.header-actions { display: flex; align-items: center; gap: 8px; }
.close-btn {
  background: none; border: none; cursor: pointer;
  color: #b0bac9; font-size: 18px; padding: 4px;
  border-radius: 6px; transition: color 0.15s;
}
.close-btn:hover { color: #212938; }
.btn-view-toggle {
  display: flex; align-items: center; gap: 6px;
  font-size: 12px; font-weight: 600;
  padding: 6px 12px; border-radius: 7px; cursor: pointer;
  border: 1px solid #d9e2ef;
  background: #f4f7fb; color: #6b7a99;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.btn-view-toggle:hover { background: #e0efff; color: #0059b2; border-color: #0059b2; }
.btn-view-toggle.active { background: #e0efff; color: #0059b2; border-color: #0059b2; }

/* ── Date bar ── */
.date-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  background: #f8fafc;
  border-bottom: 1px solid #d9e2ef;
  flex-wrap: wrap;
}
.date-icon { color: #6b7a99; font-size: 13px; }
.date-label { font-size: 12px; font-weight: 600; color: #6b7a99; }
.date-input {
  font-size: 13px; border: 1px solid #d9e2ef;
  border-radius: 6px; padding: 5px 10px;
  color: #212938; background: #ffffff;
  outline: none; cursor: pointer;
}
.date-input:focus { border-color: #0059b2; box-shadow: 0 0 0 2px rgba(0,89,178,0.12); }
.ticket-count {
  margin-left: auto;
  font-size: 12px; font-weight: 700;
  color: #6b7a99; background: #e2e8f0;
  border-radius: 12px; padding: 3px 10px;
}

/* ── Body ── */
.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
}

/* Loading */
.loading-state { display: flex; flex-direction: column; gap: 12px; }
.skeleton-row {
  height: 72px; border-radius: 10px;
  background: linear-gradient(90deg, #f0f4f8 25%, #e2e8f0 50%, #f0f4f8 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}
@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

/* Empty */
.empty-state { text-align: center; padding: 40px 0; color: #6b7a99; }
.empty-icon { font-size: 36px; color: #2db385; margin-bottom: 12px; display: block; }
.empty-state p { font-size: 14px; margin-bottom: 16px; }
.btn-look-back {
  font-size: 13px; font-weight: 600; color: #0059b2;
  background: #e0efff; border: 1px solid #0059b2;
  border-radius: 7px; padding: 7px 16px; cursor: pointer;
}
.btn-look-back:hover { background: #c5e0ff; }

/* Groups */
.wn-group { margin-bottom: 24px; }
.wn-group-header {
  display: flex; align-items: center; gap: 8px;
  font-size: 11px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.8px; color: #6b7a99;
  margin-bottom: 10px; padding-bottom: 6px;
  border-bottom: 1px solid #e8edf5;
  cursor: pointer; user-select: none;
}
.wn-group-header:hover { color: #0059b2; }
.wn-group-header:hover .wn-chevron { color: #0059b2; }
.wn-group-header i.fa-layer-group { color: #008ca6; }
.wn-group-count {
  background: #e8edf5; color: #6b7a99;
  border-radius: 10px; font-size: 11px; font-weight: 700;
  padding: 1px 8px;
}
.wn-chevron {
  margin-left: auto;
  color: #b0bac9;
  font-size: 10px;
  transition: transform 0.2s ease;
}
.wn-chevron.collapsed { transform: rotate(-90deg); }
.wn-group-body { overflow: hidden; }

/* Cards */
.wn-card {
  padding: 14px 16px;
  border: 1px solid #e8edf5;
  border-radius: 10px;
  margin-bottom: 8px;
  background: #ffffff;
  transition: border-color 0.15s;
}
.wn-card:hover { border-color: #0059b2; }
.wn-card-top {
  display: flex; align-items: flex-start;
  justify-content: space-between; gap: 12px;
  margin-bottom: 6px;
}
.wn-title {
  font-size: 14px; font-weight: 600; color: #212938;
  margin: 0; line-height: 1.4;
}
.wn-date { font-size: 11px; color: #b0bac9; white-space: nowrap; flex-shrink: 0; }
.wn-desc { font-size: 13px; color: #4a5568; margin: 0 0 8px; line-height: 1.5; }
.wn-insight {
  display: flex; align-items: flex-start; gap: 7px;
  font-size: 12px; color: #008ca6;
  background: #e5f9fd; border-radius: 6px;
  padding: 7px 10px; margin-top: 4px;
}
.wn-insight i { flex-shrink: 0; margin-top: 1px; }

/* ── Footer ── */
.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #d9e2ef;
  display: flex; justify-content: flex-end;
}
.btn-got-it {
  background: #0059b2; color: #ffffff;
  border: none; border-radius: 8px;
  padding: 10px 24px; font-size: 14px; font-weight: 600;
  cursor: pointer; transition: background 0.15s;
}
.btn-got-it:hover { background: #0073e6; }
</style>
